const MAX_SIDE = 2560;
const JPEG_QUALITY = 0.85;
const ROTATION_DEG = -30;
const WATERMARK_ALPHA = 0.2;

function replaceExtension(name: string): string {
  const withoutExt = name.replace(/\.[^./\\]+$/, '');
  const base = withoutExt.length > 0 ? withoutExt : 'photo';
  return `${base}.jpg`;
}

async function loadBitmap(
  file: File,
): Promise<{ width: number; height: number; draw: CanvasImageSource }> {
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(file);
    return { width: bitmap.width, height: bitmap.height, draw: bitmap };
  }

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve({
        width: image.naturalWidth,
        height: image.naturalHeight,
        draw: image,
      });
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    image.src = url;
  });
}

function drawWatermark(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  text: string,
): void {
  const fontSize = Math.max(16, Math.round(Math.min(width, height) * 0.045));
  ctx.save();
  ctx.globalAlpha = WATERMARK_ALPHA;
  ctx.fillStyle = '#ffffff';
  ctx.shadowColor = 'rgba(0, 0, 0, 0.55)';
  ctx.shadowBlur = Math.max(1, fontSize * 0.14);
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
  ctx.font = `600 ${fontSize}px 'Helvetica Neue', Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  ctx.translate(width / 2, height / 2);
  ctx.rotate((ROTATION_DEG * Math.PI) / 180);

  const diagonal = Math.ceil(Math.sqrt(width * width + height * height));
  const textWidth = Math.max(ctx.measureText(text).width, fontSize);
  const stepX = textWidth + fontSize * 2.5;
  const stepY = fontSize * 3.5;

  for (let y = -diagonal; y <= diagonal; y += stepY) {
    for (let x = -diagonal; x <= diagonal; x += stepX) {
      ctx.fillText(text, x, y);
    }
  }

  ctx.restore();
}

export async function applyImageWatermark(
  file: File,
  text: string,
): Promise<File> {
  const { width, height, draw } = await loadBitmap(file);
  if (width === 0 || height === 0) {
    throw new Error('Invalid image dimensions');
  }

  const scale = Math.min(1, MAX_SIDE / Math.max(width, height));
  const targetWidth = Math.max(1, Math.round(width * scale));
  const targetHeight = Math.max(1, Math.round(height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas context unavailable');
  }

  ctx.drawImage(draw, 0, 0, targetWidth, targetHeight);
  drawWatermark(ctx, targetWidth, targetHeight, text);

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob((result) => resolve(result), 'image/jpeg', JPEG_QUALITY);
  });

  if (!blob) {
    throw new Error('Failed to export watermarked image');
  }

  return new File([blob], replaceExtension(file.name), {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
}
